import { useState } from "react";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as am4charts from "@amcharts/amcharts4/charts";

interface ExportChartProps {
  chartInstanceRef: React.RefObject<am4charts.XYChart | null>;
  data: Record<string, unknown>[];
  filename: string;
}

export const ExportChart = ({
  chartInstanceRef,
  data,
  filename,
}: ExportChartProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExportCSV = () => {
    if (!data.length) return;

    // Convert data to CSV
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(","),
      ...data.map((row) =>
        headers.map((header) => `"${row[header]}"`).join(",")
      ),
    ].join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${filename}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    handleClose();
  };

  const handleExportPNG = () => {
    if (!chartInstanceRef.current) return;

    const chart = chartInstanceRef.current;
    if (chart.exporting) {
      chart.exporting.filePrefix = filename;
      chart.exporting.export("png");
    }

    handleClose();
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        aria-label="export chart"
        aria-controls={open ? "export-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
      >
        <FileDownloadIcon />
      </IconButton>

      <Menu
        id="export-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleExportCSV}>
          <ListItemText>Export data (CSV)</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleExportPNG}>
          <ListItemText>Export image (PNG)</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
